import React from 'react';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import { HealthAndSafetyIcon } from '../../icons';
import { r } from '../../theme/tokens';
import { logAnalyticsEvent } from '../../utils/analytics';
import {
  GUEST_INTERACTION_MAX_MEDICATIONS,
  addGuestMedication,
  getGuestMedications,
  removeGuestMedication,
} from '../../utils/guestMedications';
import useGuestMedications from '../../hooks/useGuestMedications';
import ComposerPanelControl from '../common/ComposerPanelControl';
import MedicationsPanel from './MedicationsPanel';

// The composer's medications control for a logged-out visitor (STACK-563
// mock, s1–s7; ruled 2026-10-08): a labelled button in the control row that
// opens the panel. Nothing shows inside the goal box.
//
// Its label says what it is for (Aravind, 2026-10-09, round 6: "+ Medications"
// did not read as the interaction check): empty, a shield-with-check and
// "Check interactions"; filled, the pill and "1 medication" / "N medications",
// the count in the words. Never "Interactions 2", which reads as two problems
// found.
//
// An added medication's RxNorm ingredient code and name go to Mixpanel, which
// is where `logAnalyticsEvent` sends, never Google (STACK-580, approved by
// Tanu 2026-10-12; docs/guest-medication-check.md §5). They are read back from
// the tab store, so what is sent is what the store kept.

export default function GuestMedicationsControl({ surface }) {
  const medications = useGuestMedications();
  const n = medications.length;

  const handleAdd = (item) => {
    const outcome = addGuestMedication(item);
    if (outcome === 'added') {
      const count = n + 1;
      // Matched by code, not position: a write the store could not make
      // (full or blocked storage) sends nulls, never the previous medication.
      // The store parses the code the same way.
      const code = Number.parseInt(item?.in_rxcui, 10);
      const added = getGuestMedications().find((m) => m.in_rxcui === code);
      logAnalyticsEvent('composer_medication_added', {
        surface,
        count,
        in_rxcui: added?.in_rxcui ?? null,
        ingredient_name: added?.ingredient_name ?? null,
      });
      if (count >= GUEST_INTERACTION_MAX_MEDICATIONS) {
        logAnalyticsEvent('composer_medication_cap_reached', {
          surface,
          cap: GUEST_INTERACTION_MAX_MEDICATIONS,
        });
      }
    }
    return outcome;
  };

  const handleRemove = (inRxcui) => {
    const remaining = removeGuestMedication(inRxcui);
    if (remaining === null) return;
    logAnalyticsEvent('composer_medication_removed', { surface, count: remaining });
  };

  const label = n ? (
    <>
      <MedicationOutlinedIcon sx={{ fontSize: r(16) }} aria-hidden />
      {n === 1 ? '1 medication' : `${n} medications`}
    </>
  ) : (
    <>
      <HealthAndSafetyIcon sx={{ fontSize: r(16) }} aria-hidden />
      Check interactions
    </>
  );

  return (
    <ComposerPanelControl
      label={label}
      ariaLabel={n
        ? `${n} ${n === 1 ? 'medication' : 'medications'} added for the interaction check`
        : 'Check interactions with your medications'}
      filled={n > 0}
      testId="composer-medications-button"
      onOpen={() => logAnalyticsEvent('composer_medications_opened', { surface })}
    >
      {({ close, sheet }) => (
        <MedicationsPanel
          medications={medications}
          onAdd={handleAdd}
          onRemove={handleRemove}
          onClose={close}
          sheet={sheet}
        />
      )}
    </ComposerPanelControl>
  );
}
